import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';
import api from '../utils/api';
import '../styles/navbar.css';
import '../styles/sidebar.css';

// Terms & Conditions content - modifying this will auto-invalidate saved consent
const TERMS_CONTENT = `
PureHD B2B E-Commerce Portal - Terms and Conditions

1. Acceptance of Terms
By accessing and using the PureHD B2B E-Commerce Portal, you accept and agree to be bound by the terms and provision of this agreement.

2. Use License
Permission is granted to temporarily access the materials on PureHD's portal for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title.

3. Account Responsibilities
You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.

4. Product Orders
All orders placed through the portal are subject to acceptance and availability. We reserve the right to refuse or cancel any order for any reason.

5. Pricing and Payment
All prices are subject to change without notice. Payment terms are as agreed upon in your business relationship with PureHD.

6. Privacy Policy
Your use of the portal is also governed by our Privacy Policy. We collect and use information as described in our Privacy Policy.

7. Limitation of Liability
In no event shall PureHD be liable for any damages arising out of the use or inability to use the materials on the portal.

8. Governing Law
These terms shall be governed by and construed in accordance with the laws of the State of Massachusetts, USA.

9. Contact Information
For questions about these Terms & Conditions, please contact us at:
PureHD
3 Mill & Main Place, Suite #200
Maynard, MA 01754-2657
Phone: (888) 787-3431
`;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isDesktop = windowWidth >= 992;

  // Terms & Conditions state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [termsVersion, setTermsVersion] = useState('');

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Check for session expired error in URL
    const params = new URLSearchParams(location.search);
    if (params.get('error') === 'session_expired') {
      setError('Your session has expired. Please login again.');
    }
  }, [location]);

  // Fetch Terms & Conditions from database
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await api.get('/admin/terms/public');
        setTermsContent(response.data.content);
        setTermsVersion(response.data.version);
      } catch (err) {
        console.error('Failed to load Terms & Conditions:', err);
        // Fallback to hardcoded content if API fails
        setTermsContent(TERMS_CONTENT);
      }
    };

    fetchTerms();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(username, password, rememberMe);

      // Check if user is admin - redirect to admin dashboard
      if (response && response.is_admin) {
        navigate('/admin');
        return;
      }

      // Check if THIS USER has accepted terms (user-specific, not browser-specific)
      if (response && !response.terms_accepted) {
        // User hasn't accepted terms yet - show modal
        setLoading(false);
        setShowTermsModal(true);
        setHasScrolledToBottom(false);
        return; // Don't navigate yet
      }

      // User has already accepted terms - proceed to dashboard
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleTermsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const scrolledToBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
    if (scrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAgreeToTerms = async () => {
    try {
      // Call backend API to save terms acceptance for THIS USER
      await api.post('/auth/accept-terms');

      // Terms saved successfully - close modal and go to dashboard
      setTermsAgreed(true);
      setShowTermsModal(false);
      navigate('/');
    } catch (err) {
      console.error('Failed to save terms acceptance:', err);
      setError('Failed to save terms acceptance. Please try again.');
      setShowTermsModal(false); // Close modal so user can see the error
    }
  };

  return (
    <>
      {/* Simple, clean header */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1040,
        backgroundColor: '#4D64C1',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        height: '70px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 20px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Logo on the left */}
          <a href="https://purehd.com" target="_blank" rel="noopener noreferrer">
            <img
              src="/img/PureHD_Logo.png"
              alt="PureHD Logo"
              style={{
                height: '45px',
                width: 'auto',
                display: 'block',
                filter: 'brightness(0) invert(1)',
                objectFit: 'contain'
              }}
            />
          </a>

          {/* Contact info on the right */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '30px'
          }}>
            <div style={{
              color: 'white',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-phone-alt" style={{ fontSize: '13px', opacity: 0.9 }}></i>
              <span>Questions? Call</span>
              <span style={{
                fontWeight: '600'
              }}>(888) 787-3431</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div style={{
        minHeight: '100vh',
        paddingTop: '70px',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div className="container" style={{ padding: '40px 15px' }}>
          <div className="row justify-content-center">
            <div className="col-md-10 col-lg-8 col-xl-5">
              <div className="mx-auto" style={{ maxWidth: '440px' }}>

                {/* Welcome Message Card */}
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '25px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}>
                  <h2 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>Welcome to PureHD eCommerce Center</h2>
                  <p style={{
                    fontSize: '15px',
                    color: '#546e7a',
                    textAlign: 'center',
                    marginBottom: 0,
                    lineHeight: '1.6'
                  }}>
                    Easy ordering of PureHD <strong style={{ textDecoration: 'underline' }}>Room</strong> Equipment:
                    <br />
                    <strong style={{ color: '#4D64C1' }}>Set Top Boxes • Remotes • Power Supplies • Cables</strong>
                  </p>
                </div>

                {/* Login Card */}
                <div className="card" style={{
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  border: 'none',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    backgroundColor: '#4D64C1',
                    color: 'white',
                    padding: '25px',
                    textAlign: 'center'
                  }}>
                    <i className="fas fa-user-circle" style={{ fontSize: '40px', marginBottom: '10px', opacity: 0.9 }}></i>
                    <h1 style={{ fontSize: '22px', margin: 0, fontWeight: '600' }}>Sign In to Your Account</h1>
                  </div>
                  <div className="card-body" style={{ padding: '35px 30px' }}>

                    {error && (
                      <div className="alert alert-danger d-flex align-items-center">
                        <i className="fas fa-times-circle flex-shrink-0 me-3"></i>
                        <div>{error}</div>
                      </div>
                    )}
                    
                    <form onSubmit={handleSubmit}>
                      <div className="mb-4">
                        <label htmlFor="username" className="form-label" style={{ fontWeight: '500', color: '#2c3e50', marginBottom: '8px' }}>Username</label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                            <i className="fas fa-user" style={{ color: '#4D64C1' }}></i>
                          </span>
                          <input
                            type="text"
                            id="username"
                            name="username"
                            className="form-control"
                            required
                            autoFocus
                            placeholder="Enter your username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            disabled={loading}
                            style={{ borderLeft: 'none' }}
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="password" className="form-label" style={{ fontWeight: '500', color: '#2c3e50', marginBottom: '8px' }}>Password</label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                            <i className="fas fa-lock" style={{ color: '#4D64C1' }}></i>
                          </span>
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            className="form-control"
                            required
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            style={{ borderLeft: 'none', borderRight: 'none' }}
                          />
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={togglePasswordVisibility}
                            disabled={loading}
                            style={{ borderLeft: 'none' }}
                          >
                            <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`}></i>
                          </button>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="rememberMe"
                            name="remember"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            disabled={loading}
                            style={{ cursor: 'pointer' }}
                          />
                          <label className="form-check-label" htmlFor="rememberMe" style={{ cursor: 'pointer', userSelect: 'none' }}>Remember me</label>
                        </div>
                        <Link
                          to="/forgot-password"
                          style={{
                            color: '#4D64C1',
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          Forgot Password?
                        </Link>
                      </div>

                      <div className="d-grid mb-3">
                        <button
                          className="btn btn-primary btn-lg"
                          type="submit"
                          disabled={loading}
                          style={{
                            backgroundColor: loading ? '#cccccc' : '#4D64C1',
                            border: 'none',
                            padding: '12px',
                            fontSize: '16px',
                            fontWeight: '500',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease',
                            cursor: loading ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Signing In...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-sign-in-alt me-2"></i> Sign In
                            </>
                          )}
                        </button>
                      </div>

                      <div style={{
                        textAlign: 'center',
                        paddingTop: '15px',
                        borderTop: '1px solid #e9ecef',
                        marginTop: '20px'
                      }}>
                        <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '8px' }}>
                          Need assistance with your account?
                        </p>
                        <span
                          style={{
                            color: '#4D64C1',
                            fontWeight: '600',
                            fontSize: '16px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <i className="fas fa-phone-alt"></i>
                          (888) 787-3431
                        </span>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="modal show d-block" tabIndex={-1} style={{
          backgroundColor: 'rgba(0,0,0,0.5)'
        }}>
          <div className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-xl" style={{
            maxWidth: '900px',
            maxHeight: '900px'
          }}>
            <div className="modal-content" style={{
              borderRadius: '12px',
              maxHeight: '900px'
            }}>
              <div className="modal-header" style={{ backgroundColor: '#4D64C1', color: 'white', flex: '0 0 auto' }}>
                <h5 className="modal-title">Terms & Conditions</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowTermsModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div
                className="modal-body"
                onScroll={handleTermsScroll}
                style={{
                  flex: '1 1 auto',
                  overflowY: 'auto',
                  padding: '30px 30px 0 30px',
                  maxHeight: 'calc(900px - 160px)'
                }}
              >
                {termsVersion && (
                  <p style={{ marginBottom: '15px', fontSize: '12px', color: '#6c757d' }}>
                    Version {termsVersion}
                  </p>
                )}
                <div style={{ lineHeight: '1.6' }}>
                  {termsContent.split('\n').map((line, index) => {
                    // Main title (all caps or starts with specific text)
                    if (line.includes('Terms and Conditions') || line.includes('TERMS AND CONDITIONS')) {
                      return <h6 key={index} style={{ fontWeight: 'bold', marginBottom: '15px' }}>{line}</h6>;
                    }
                    // Numbered headers (e.g., "1. Acceptance of Terms")
                    if (/^\d+\.\s+[A-Z]/.test(line)) {
                      return <h6 key={index} style={{ fontWeight: 'bold', marginTop: '20px', marginBottom: '10px' }}>{line}</h6>;
                    }
                    // Empty lines
                    if (line.trim() === '') {
                      return <br key={index} />;
                    }
                    // Regular paragraphs
                    return <p key={index} style={{ marginBottom: '10px' }}>{line}</p>;
                  })}
                </div>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '15px',
                padding: '20px',
                borderTop: '1px solid #e9ecef',
                flex: '0 0 auto'
              }}>
                <button
                  type="button"
                  onClick={() => setShowTermsModal(false)}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 30px',
                    fontSize: '16px',
                    fontWeight: '500',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleAgreeToTerms}
                  disabled={!hasScrolledToBottom}
                  style={{
                    backgroundColor: hasScrolledToBottom ? '#4D64C1' : '#cccccc',
                    color: 'white',
                    border: 'none',
                    padding: '12px 30px',
                    fontSize: '16px',
                    fontWeight: '500',
                    borderRadius: '8px',
                    cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Agree
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default Login; 